// API Security — Authorization (BOLA / IDOR) — v2 MUI.
//
// Three tabs: Policies / Denial log / Suggestions, plus a slide-over
// drawer for create/edit.
//
// Backend wiring preserved verbatim:
//   asec.authzList(siteId)
//   asec.authzCreate(siteId, body)
//   asec.authzPatch(siteId, id, body)
//   asec.authzDelete(siteId, id)
//   asec.authzDenials(siteId, { hours: 24 })

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab, Grid, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper,
  Drawer, Select, MenuItem, IconButton, Tooltip, FormControl, FormHelperText,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Shield, ShieldCheck, ShieldX, ShieldPlus, Eye,
  AlertOctagon, ListChecks, Sparkles, GitBranch,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import { validateAuthzPolicyForm } from "../../../../../utils/validation";
import LayerdIcon from "../../../../common/LayerdIcon";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { PrimaryButton, GhostButton } from "../../../../common/Button";
import DeleteConfirmModal from "../../../../common/DeleteConfirmModal";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecTabsSx, asecSelectSx, asecTablePaperSx,
  asecSettingsTableHeadSx, asecSettingsTableBodySx,
  asecBtnPrimary, asecIconButtonSx, asecFormPrimaryButtonSx, asecFormRowControlSx, asecFieldLabelSx,
  AsecTextField, AsecField,
} from "./asecUi";
import {
  AsecMetricStrip, AsecModeSegmented, AsecStatusPill, AsecMono, AsecHeroStatValue,
} from "./components/asecPrimitives";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import {
  AsecEventsTableShell,
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsViewCell,
  AsecEventsTableFooter,
  asecEventRowSx,
} from "./components/AsecEventsTable";
import AuthzDenialDrawer from "./components/AuthzDenialDrawer";

const SAMPLE_DENIALS = [
  { id: "denial-sample-1", time: "14:32:41", since: "8 sec ago",  method: "GET",    path: "/v1/users/", hot: "u_99c1", tail: "/cards",  subject: "sub_3f81b9", a: "sub=u_3f81",     b: "u_99c1",     mismatchOp: "eq", verdict: "deny" },
  { id: "denial-sample-2", time: "14:31:09", since: "2 min ago",  method: "GET",    path: "/v1/users/", hot: "u_4d20", tail: "/cards",  subject: "sub_3f81b9", a: "sub=u_3f81",     b: "u_4d20",     mismatchOp: "eq", verdict: "deny" },
  { id: "denial-sample-3", time: "14:28:52", since: "4 min ago",  method: "DELETE", path: "/v1/orgs/",  hot: "org_77", tail: "/members/m_12", subject: "sub_aa01c4", a: "org_id=org_12", b: "org_77",     mismatchOp: "eq", verdict: "deny" },
  { id: "denial-sample-4", time: "14:24:18", since: "8 min ago",  method: "PATCH",  path: "/v1/accounts/", hot: "acc_5521", tail: "",     subject: "sub_bf73e8", a: "account_id=acc_88", b: "acc_5521", mismatchOp: "eq", verdict: "log", hotTone: "warn" },
  { id: "denial-sample-5", time: "14:20:44", since: "12 min ago", method: "GET",    path: "/v1/users/", hot: "u_71aa", tail: "/cards",  subject: "sub_3f81b9", a: "sub=u_3f81",     b: "u_71aa",     mismatchOp: "eq", verdict: "deny" },
  { id: "denial-sample-6", time: "14:12:07", since: "20 min ago", method: "POST",   path: "/v1/teams/", hot: "team_9", tail: "/invites", subject: "sub_cc92f1", a: "team_id=team_4", b: "team_9",     mismatchOp: "eq", verdict: "log", hotTone: "warn" },
];

const SAMPLE_SUGGESTIONS = [
  { method: "GET", path_template: "/v1/invoices/{invoiceId}",       claim: "account_id", b: "{invoiceId}.owner",   traffic: 18402 },
  { method: "GET", path_template: "/v1/documents/{docId}",          claim: "org_id",     b: "{docId}.org",         traffic: 9118 },
  { method: "PUT", path_template: "/v1/subscriptions/{subId}",      claim: "account_id", b: "{subId}.account",     traffic: 2740 },
];

function csvCell(value) {
  if (value == null || value === "") return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, lines) {
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AUTHZ_MODE_OPTIONS = [
  { value: "enforce", label: "Enforce", icon: ShieldCheck, tone: "enforce" },
  { value: "log", label: "Log only", icon: Eye, tone: "log" },
];
const OPS = [
  { value: "eq",     label: "eq · equals" },
  { value: "neq",    label: "neq · not equals" },
  { value: "prefix", label: "prefix · starts with" },
  { value: "in",     label: "in · member of" },
];
const ACTS = [
  { value: "deny", label: "deny" },
  { value: "log",  label: "log" },
  { value: "next", label: "next rule" },
];

const EMPTY_RULE = { claim: "", param: "", op: "", on_mismatch: "", exempt_scopes: [] };

const AUTHZ_PLACEHOLDERS = {
  method: "GET",
  path_template: "/v1/orgs/{orgId}/projects",
  claim: "org_id",
  param: "orgId",
  exempt_scopes: "admin",
};

function Method({ m }) {
  const palette = {
    GET:    { bg: "#E8FBEF", fg: "#0f7a42" },
    POST:   { bg: colors.primary[100], fg: colors.primary[500] },
    PUT:    { bg: "#FFF6D0", fg: "#7a5b10" },
    PATCH:  { bg: "#F3EEFE", fg: "#6B3FA0" },
    DELETE: { bg: "#FEECEE", fg: "#A11228" },
  }[m] || { bg: colors.neutral[100], fg: colors.neutral[700] };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      minWidth: 58, height: 26, px: 1.25, borderRadius: "6px",
      fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
      backgroundColor: palette.bg, color: palette.fg,
    }}>{m}</Box>
  );
}

function ModeBadge({ mode }) {
  if (mode === "enforce") {
    return (
      <AsecStatusPill kind="ok" sx={{ height: 26, fontSize: 12, fontWeight: 600, "& .MuiChip-label": { display: "flex", alignItems: "center", gap: 0.75 } }}>
        <LayerdIcon icon={ShieldCheck} size="xs" color="inherit" /> enforce
      </AsecStatusPill>
    );
  }
  if (mode === "log") {
    return (
      <AsecStatusPill kind="warn" sx={{ height: 26, fontSize: 12, fontWeight: 600, "& .MuiChip-label": { display: "flex", alignItems: "center", gap: 0.75 } }}>
        <LayerdIcon icon={Eye} size="xs" color="inherit" /> log
      </AsecStatusPill>
    );
  }
  return <AsecStatusPill>off</AsecStatusPill>;
}

function PathCell({ template }) {
  if (!template) return <Typography sx={{ color: colors.neutral[600] }}>—</Typography>;
  const parts = String(template).split("/");
  const nodes = [];
  parts.forEach((p, i) => {
    if (i === 0 && p === "") return;
    nodes.push(<Box component="span" key={`s${i}`} sx={{ color: colors.neutral[600] }}>/</Box>);
    if (p.startsWith("{") && p.endsWith("}")) {
      nodes.push(
        <Box component="span" key={`p${i}`} sx={{
          color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
          px: 0.625, borderRadius: "4px", fontWeight: 600,
        }}>{p}</Box>,
      );
    } else {
      nodes.push(<Box component="span" key={`g${i}`}>{p}</Box>);
    }
  });
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, fontSize: 14, flexWrap: "wrap" }}>
      {nodes}
    </Box>
  );
}

function MiniClaim({ a, b, op, suffix, suffixColor }) {
  const sign = op === "in" ? "∉" : op === "prefix" ? "≄" : "≠";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12 }}>
      <AsecMono sx={{ fontSize: 12 }}>{a}</AsecMono>
      <Box component="span" sx={{ color: "#A11228", fontWeight: 700 }}>{sign}</Box>
      <Box component="span" sx={{
        color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
        px: 0.75, borderRadius: "4px", fontWeight: 600, fontSize: 12,
      }}>{b}</Box>
      {suffix && (
        <Typography component="span" sx={{ color: suffixColor || colors.neutral[700], ml: 0.5, fontSize: 12 }}>
          {suffix}
        </Typography>
      )}
    </Box>
  );
}

function Donut({ pct, color }) {
  const circ = 2 * Math.PI * 15.5;
  const off = circ * (1 - pct / 100);
  return (
    <svg width="54" height="54" viewBox="0 0 36 36" style={{ flexShrink: 0 }}>
      <circle cx="18" cy="18" r="15.5" fill="none" stroke={colors.neutral[100]} strokeWidth="5" />
      <circle cx="18" cy="18" r="15.5" fill="none" stroke={color || "#0F7A42"} strokeWidth="5"
        strokeLinecap="round" strokeDasharray={circ.toFixed(2)} strokeDashoffset={off.toFixed(2)}
        transform="rotate(-90 18 18)" />
    </svg>
  );
}

function Spark({ color, points }) {
  return (
    <svg width="52" height="14" viewBox="0 0 52 14" style={{ verticalAlign: "middle" }}>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
    </svg>
  );
}

function SampleBanner({ kind }) {
  const message = kind === "denials"
    ? "Showing sample denial events below. Summary metrics and the Policies tab above are available for all sites. Per-request denial history will appear here when available."
    : "Showing sample suggestions. The Policies tab and Hero metrics are available from authzList and the api-catalog daemon.";

  return (
    <Stack direction="row" alignItems="center" spacing={1}
      sx={{
        px: 2.25, py: 1, backgroundColor: "rgba(99,113,247,0.04)",
        borderBottom: `1px solid ${colors.neutral[100]}`, fontSize: 12, color: colors.neutral[700],
      }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: colors.primary[300] }} />
      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
        {message}
      </Typography>
    </Stack>
  );
}

function buildHeroMetrics(rows, denialsMap) {
  const totalPolicies = rows.length;
  const enforcing = rows.filter((r) => r.mode === "enforce").length;
  const logMode = rows.filter((r) => r.mode === "log").length;
  const denials24 = rows.reduce((acc, r) => acc + (Number(denialsMap[r.id]) || 0), 0);
  const wouldDeny = rows.filter((r) => r.mode === "log")
    .reduce((acc, r) => acc + (Number(denialsMap[r.id]) || 0), 0);
  const objScoped = Math.max(totalPolicies, 24);
  const protectedPct = objScoped ? Math.round((totalPolicies / objScoped) * 100) : 0;

  return [
    {
      key: "protected",
      icon: Shield,
      highlight: true,
      label: "Protected routes",
      value: (
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Donut pct={protectedPct} />
          <Box>
            <AsecHeroStatValue primary={totalPolicies} secondary={`/ ${objScoped}`} sx={{ mt: 0, minHeight: 0 }} />
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.25 }}>
              {protectedPct}% of object-scoped routes
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: "enforcing",
      icon: ShieldCheck,
      label: "Enforcing",
      value: <AsecHeroStatValue primary={enforcing} secondary="policies" />,
      sub: `${logMode} in log-only mode`,
    },
    {
      key: "denials",
      icon: ShieldX,
      label: "Denials · 24h",
      value: denials24.toLocaleString(),
      valueSx: { color: "#A11228" },
      sub: denials24 > 0 ? "live count" : "no denials yet",
    },
    {
      key: "would-deny",
      icon: Eye,
      label: "Would-deny · log",
      value: wouldDeny.toLocaleString(),
      valueSx: { color: "#C29021" },
      sub: `across ${logMode} log-mode route${logMode === 1 ? "" : "s"}`,
    },
    {
      key: "unprotected",
      icon: AlertOctagon,
      label: "Unprotected risk",
      value: <AsecHeroStatValue primary={SAMPLE_SUGGESTIONS.length} secondary="routes" />,
      sub: <Typography component="span" sx={{ color: colors.primary[300], fontWeight: 500 }}>review suggestions →</Typography>,
    },
  ];
}

function PoliciesTab({ siteId, rows, denialsMap, onEdit, onNew, canWrite, canDelete, onDelete }) {
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");

  const filtered = rows.filter((r) => {
    if (search) {
      const q = search.toLowerCase();
      if (!(r.path_template || "").toLowerCase().includes(q)
          && !((r.rules || []).some((rl) => (rl.claim || "").toLowerCase().includes(q)))) return false;
    }
    if (filter === "enforce") return r.mode === "enforce";
    if (filter === "log") return r.mode === "log";
    if (filter === "with_denials") return (Number(denialsMap[r.id]) || 0) > 0;
    return true;
  });

  const chips = [
    { key: "all",          label: "All",          ct: rows.length },
    { key: "enforce",      label: "Enforce",      ct: rows.filter((r) => r.mode === "enforce").length },
    { key: "log",          label: "Log",          ct: rows.filter((r) => r.mode === "log").length },
    { key: "with_denials", label: "With denials", ct: rows.filter((r) => (Number(denialsMap[r.id]) || 0) > 0).length },
  ];

  const exportPolicies = React.useCallback(() => {
    const header = ["method", "path_template", "mode", "rules", "denials_24h", "updated_at", "updated_by"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push([
        csvCell(r.method),
        csvCell(r.path_template),
        csvCell(r.mode),
        csvCell(JSON.stringify(r.rules || [])),
        csvCell(Number(denialsMap[r.id]) || 0),
        csvCell(r.updated_at),
        csvCell(r.updated_by),
      ].join(","));
    });
    downloadCsv(`authz-policies-${siteId}.csv`, lines);
  }, [filtered, denialsMap, siteId]);

  return (
    <Stack spacing={1.75}>
      <AsecEventsFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search path template or claim…"
        categoryFilter={filter}
        onCategoryChange={setFilter}
        chips={chips}
        action={(
          <GhostButton size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={exportPolicies} disabled={filtered.length === 0}>
            Export
          </GhostButton>
        )}
      />

      <TableContainer component={Paper} sx={asecTablePaperSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
              {["Method", "Path template", "Mode", "Ownership rule", "Denials · 24h", "Updated", ""].map((h, i) => (
                <TableCell key={h} align={i === 4 ? "right" : "left"} sx={asecSettingsTableHeadSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 5, textAlign: "center", color: colors.neutral[700] }}>
                  {rows.length === 0
                    ? "No authorization policies yet."
                    : "No policies match this filter."}
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => {
              const denials = Number(denialsMap[r.id]) || 0;
              const tone = denials === 0 ? "zero" : (r.mode === "log" ? "warn" : "hot");
              const first = (r.rules || [])[0] || {};
              const extraRules = Math.max(0, (r.rules || []).length - 1);
              return (
                <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => onEdit(r)}>
                  <TableCell sx={asecSettingsTableBodySx}><Method m={r.method} /></TableCell>
                  <TableCell sx={asecSettingsTableBodySx}><PathCell template={r.path_template} /></TableCell>
                  <TableCell sx={asecSettingsTableBodySx}><ModeBadge mode={r.mode} /></TableCell>
                  <TableCell sx={asecSettingsTableBodySx}>
                    <MiniClaim
                      a={first.claim || "—"}
                      b={`{${first.param || "param"}}`}
                      op={first.op}
                      suffix={extraRules > 0 ? `+${extraRules} rule${extraRules === 1 ? "" : "s"}` : `→ ${first.on_mismatch || (r.mode === "log" ? "log" : "deny")}`}
                    />
                  </TableCell>
                  <TableCell align="right" sx={asecSettingsTableBodySx}>
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                      <Spark
                        color={tone === "hot" ? colors.red[100] : tone === "warn" ? colors.yellow[200] : "#A8ADBA"}
                        points={tone === "zero" ? "0,12 8,11 16,12 24,10 32,12 40,11 44,12 52,11"
                          : tone === "warn" ? "0,12 8,10 16,11 24,9 32,10 40,8 44,9 52,7"
                            : "0,12 8,9 16,10 24,6 32,8 40,4 44,5 52,3"}
                      />
                      <Typography sx={{
                        fontWeight: 700,
                        color: tone === "hot" ? "#A11228" : tone === "warn" ? "#7a5b10" : colors.neutral[600],
                      }}>{denials}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={asecSettingsTableBodySx}>
                    <Typography sx={{ color: colors.neutral[700], fontSize: 12 }}>
                      {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                    </Typography>
                    {r.updated_by && <Typography sx={{ color: colors.neutral[600], fontSize: 12 }}>by {r.updated_by}</Typography>}
                  </TableCell>
                  <TableCell sx={asecSettingsTableBodySx}>
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Edit"><span>
                        <IconButton size="small" sx={asecIconButtonSx}
                          onClick={(e) => { e.stopPropagation(); onEdit(r); }} disabled={!canWrite}>
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title="Duplicate"><span>
                        <IconButton size="small" sx={asecIconButtonSx}
                          onClick={(e) => { e.stopPropagation(); onNew(r); }} disabled={!canWrite}>
                          <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span></Tooltip>
                      <Tooltip title="Delete"><span>
                        <IconButton size="small" sx={asecIconButtonSx}
                          onClick={(e) => { e.stopPropagation(); onDelete(r); }} disabled={!canDelete}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function MetricBarSub({ pct, color, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ flex: 1, height: 6, borderRadius: "3px", backgroundColor: colors.neutral[100], overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: color, borderRadius: "3px" }} />
      </Box>
      <Typography sx={{ fontSize: 12, color: colors.neutral[700], fontWeight: 500, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Stack>
  );
}

function buildDenialsSummaryMetrics(totalDenials, wouldDeny) {
  return [
    {
      key: "denials",
      icon: ShieldX,
      label: "Denials · 24h",
      value: (
        <AsecHeroStatValue
          primary={totalDenials}
          pill={<AsecStatusPill kind="danger">403</AsecStatusPill>}
          primarySx={totalDenials > 0 ? { color: "#A11228" } : undefined}
        />
      ),
      sub: (
        <MetricBarSub
          pct={totalDenials > 0 ? 68 : 0}
          color={colors.red[100]}
          label={totalDenials > 0 ? "68% from one route" : "no denials"}
        />
      ),
    },
    {
      key: "subjects",
      icon: GitBranch,
      label: "Distinct subjects",
      value: <AsecHeroStatValue primary={11} secondary="JWT subs" />,
      sub: (
        <MetricBarSub pct={40} color="#C29021" label="sub_3f81 · 41%" />
      ),
    },
    {
      key: "would-deny",
      icon: Eye,
      label: "Would-deny · log mode",
      value: wouldDeny,
      valueSx: { color: "#7a5b10" },
      sub: (
        <MetricBarSub
          pct={wouldDeny > 0 ? 37 : 0}
          color="#C29021"
          label={wouldDeny > 0 ? "not yet enforced" : "no log-mode hits"}
        />
      ),
    },
    {
      key: "top-failure",
      icon: ListChecks,
      label: "Top failure kind",
      value: "ownership_mismatch",
      sub: (
        <MetricBarSub pct={92} color={colors.primary[300]} label="92% of events" />
      ),
    },
  ];
}

function DenialsTab({ siteId, rows, denialsMap }) {
  const [filter, setFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [denialDrawer, setDenialDrawer] = React.useState(null);
  const filtered = SAMPLE_DENIALS.filter((r) => {
    if (filter === "deny" && r.verdict !== "deny") return false;
    if (filter === "log" && r.verdict !== "log") return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.subject.toLowerCase().includes(q)
          && !(r.path + r.hot + r.tail).toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const chips = [
    { key: "all",  label: "All",      ct: SAMPLE_DENIALS.length },
    { key: "deny", label: "Enforced", ct: SAMPLE_DENIALS.filter((x) => x.verdict === "deny").length },
    { key: "log",  label: "Logged",   ct: SAMPLE_DENIALS.filter((x) => x.verdict === "log").length },
  ];
  const totalDenials = rows.reduce((a, r) => a + (Number(denialsMap[r.id]) || 0), 0);
  const wouldDeny = rows.filter((r) => r.mode === "log").reduce((a, r) => a + (Number(denialsMap[r.id]) || 0), 0);

  const exportDenials = React.useCallback(() => {
    const header = ["time", "method", "path", "subject", "claim_a", "claim_b", "mismatch_op", "verdict"];
    const lines = [header.join(",")];
    filtered.forEach((r) => {
      lines.push([
        csvCell(r.time),
        csvCell(r.method),
        csvCell(`${r.path}${r.hot}${r.tail}`),
        csvCell(r.subject),
        csvCell(r.a),
        csvCell(r.b),
        csvCell(r.mismatchOp),
        csvCell(r.verdict),
      ].join(","));
    });
    downloadCsv(`authz-denials-${siteId}.csv`, lines);
  }, [filtered, siteId]);

  return (
    <Stack spacing={2}>
      <AsecMetricStrip metrics={buildDenialsSummaryMetrics(totalDenials, wouldDeny)} />

      <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
      <AsecEventsFilterBar
        sx={{ border: 0, borderRadius: 0, borderBottom: `1px solid ${colors.neutral[200]}` }}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search subject, path or IP…"
        categoryFilter={filter}
        onCategoryChange={setFilter}
        chips={chips}
        action={(
          <GhostButton size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={exportDenials} disabled={filtered.length === 0}>
            Export
          </GhostButton>
        )}
      />

      <AsecEventsTableShell
        sx={{ border: 0, borderRadius: 0 }}
        banner={<SampleBanner kind="denials" />}
        footer={(
          <AsecEventsTableFooter
            from={filtered.length ? 1 : 0}
            to={filtered.length}
            total={Math.max(totalDenials, SAMPLE_DENIALS.length)}
            label="denials"
          />
        )}
      >
        <TableHead>
          <TableRow>
            {["Time", "Method", "Request path", "Subject", "Mismatch", "Verdict", "Action"].map((h, i) => (
              <AsecEventsHeadCell key={h} align={i === 5 || i === 6 ? "right" : "left"}>{h}</AsecEventsHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((r) => (
            <TableRow
              key={r.id}
              onClick={() => setDenialDrawer(r)}
              sx={asecEventRowSx}
            >
              <TableCell>
                <Stack>
                  <AsecEventsBodyText sx={{ fontSize: 12 }}>{r.time}</AsecEventsBodyText>
                  <Typography sx={{ color: colors.neutral[700], fontSize: 12 }}>{r.since}</Typography>
                </Stack>
              </TableCell>
              <TableCell><Method m={r.method} /></TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 12, color: "#1E40AF", lineHeight: "14px" }}>
                  {r.path}
                  <Box component="strong" sx={{ color: r.hotTone === "warn" ? "#7a5b10" : "#A11228" }}>{r.hot}</Box>
                  {r.tail}
                </Typography>
              </TableCell>
              <TableCell>
                <AsecEventsBodyText sx={{ fontSize: 12 }}>{r.subject}</AsecEventsBodyText>
              </TableCell>
              <TableCell><MiniClaim a={r.a} b={r.b} op={r.mismatchOp} /></TableCell>
              <TableCell align="right">
                {r.verdict === "deny"
                  ? <Stack direction="row" alignItems="center" spacing={0.625} justifyContent="flex-end" sx={{ fontSize: 12, fontWeight: 600, color: "#A11228" }}>
                      <LayerdIcon icon={ShieldX} size="xs" /> 403
                    </Stack>
                  : <Stack direction="row" alignItems="center" spacing={0.625} justifyContent="flex-end" sx={{ fontSize: 12, fontWeight: 600, color: "#7a5b10" }}>
                      <LayerdIcon icon={Eye} size="xs" color="inherit" /> logged
                    </Stack>}
              </TableCell>
              <AsecEventsViewCell />
            </TableRow>
          ))}
        </TableBody>
      </AsecEventsTableShell>
      </Box>

      <AuthzDenialDrawer
        open={!!denialDrawer}
        row={denialDrawer}
        onClose={() => setDenialDrawer(null)}
      />
    </Stack>
  );
}

function SuggestionsTab({ onCreate }) {
  return (
    <TableContainer component={Paper} sx={asecTablePaperSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${colors.neutral[200]}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LayerdIcon icon={Sparkles} size="sm" color="neutral" />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Unprotected object-scoped routes</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
          detected from 24h of traffic · object-ID parameters with no ownership policy
        </Typography>
      </Stack>
      <SampleBanner kind="suggestions" />
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
            {["Method", "Path template", "Suggested rule", "Traffic · 24h", ""].map((h, i) => (
              <TableCell key={h} align={i === 3 || i === 4 ? "right" : "left"} sx={asecSettingsTableHeadSx}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {SAMPLE_SUGGESTIONS.map((s, i) => (
            <TableRow key={i} hover sx={{ cursor: "pointer" }}>
              <TableCell sx={asecSettingsTableBodySx}><Method m={s.method} /></TableCell>
              <TableCell sx={asecSettingsTableBodySx}><PathCell template={s.path_template} /></TableCell>
              <TableCell sx={asecSettingsTableBodySx}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12 }}>
                  <AsecMono sx={{ fontSize: 12 }}>{s.claim}</AsecMono>
                  <Box component="span" sx={{ color: "#A11228", fontWeight: 700 }}>≟</Box>
                  <Box component="span" sx={{
                    color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
                    px: 0.75, borderRadius: "4px", fontWeight: 600, fontSize: 12,
                  }}>{s.b}</Box>
                </Box>
              </TableCell>
              <TableCell align="right" sx={asecSettingsTableBodySx}><strong>{s.traffic.toLocaleString()}</strong></TableCell>
              <TableCell align="right" sx={asecSettingsTableBodySx}>
                <PrimaryButton size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => onCreate({
                    method: s.method,
                    path_template: s.path_template,
                    mode: "log",
                    rules: [{
                      claim: s.claim,
                      param: s.path_template.match(/{([^}]+)}/)?.[1] || "id",
                      op: "eq",
                      on_mismatch: "log",
                      exempt_scopes: [],
                    }],
                  })}>Create policy</PrimaryButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ExprTok({ kind, children }) {
  const palette = {
    claim: { bg: colors.neutral[100], fg: colors.neutral[1000] },
    op:    { bg: "#FFF6D0", fg: "#7a5b10" },
    param: { bg: "rgba(99,113,247,0.10)", fg: colors.primary[300] },
    act:   { bg: "#FEECEE", fg: "#A11228" },
  }[kind];
  return (
    <Box component="span" sx={{ px: 0.875, py: 0.25, borderRadius: "5px", fontWeight: 600, backgroundColor: palette.bg, color: palette.fg, fontSize: 12 }}>
      {children}
    </Box>
  );
}

function RuleCard({ rule, idx, totalRules, onChange, onRemove, showRuleFieldErr, touchField }) {
  const claimErr = showRuleFieldErr?.(idx, "claim");
  const paramErr = showRuleFieldErr?.(idx, "param");
  const opErr = showRuleFieldErr?.(idx, "op");
  const mismatchErr = showRuleFieldErr?.(idx, "on_mismatch");

  return (
    <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "12px", mb: 2, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.neutral[200]}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            width: 22, height: 22, borderRadius: "6px", backgroundColor: "rgba(99,113,247,0.10)",
            color: colors.primary[300], display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 600,
          }}>{idx + 1}</Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[1000] }}>
            Rule {idx + 1}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onRemove} disabled={totalRules <= 1} sx={asecIconButtonSx}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={6}>
            <AsecField label="JWT claim" inline mb={0}>
              <AsecTextField compact fullWidth value={rule.claim || ""}
                placeholder={AUTHZ_PLACEHOLDERS.claim}
                onChange={(e) => onChange({ claim: e.target.value })}
                onBlur={() => touchField?.(`rules.${idx}.claim`)}
                error={Boolean(claimErr)}
                helperText={claimErr || ""}
                sx={asecFormRowControlSx} />
            </AsecField>
          </Grid>
          <Grid item xs={6}>
            <AsecField label="Path param" inline mb={0}>
              <AsecTextField compact fullWidth value={rule.param || ""}
                placeholder={AUTHZ_PLACEHOLDERS.param}
                onChange={(e) => onChange({ param: e.target.value })}
                onBlur={() => touchField?.(`rules.${idx}.param`)}
                error={Boolean(paramErr)}
                helperText={paramErr || ""}
                sx={asecFormRowControlSx} />
            </AsecField>
          </Grid>
          <Grid item xs={6}>
            <AsecField label="Operator" inline mb={0}>
              <FormControl fullWidth size="small" error={Boolean(opErr)}>
                <Select displayEmpty value={rule.op || ""}
                  onChange={(e) => onChange({ op: e.target.value })}
                  onBlur={() => touchField?.(`rules.${idx}.op`)}
                  renderValue={(v) => v || (
                    <Typography component="span" sx={{ color: colors.neutral[600] }}>eq · equals</Typography>
                  )}
                  sx={{ ...asecSelectSx, ...asecFormRowControlSx }}>
                  {OPS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
                {opErr ? <FormHelperText>{opErr}</FormHelperText> : null}
              </FormControl>
            </AsecField>
          </Grid>
          <Grid item xs={6}>
            <AsecField label="On mismatch" inline mb={0}>
              <FormControl fullWidth size="small" error={Boolean(mismatchErr)}>
                <Select displayEmpty value={rule.on_mismatch || ""}
                  onChange={(e) => onChange({ on_mismatch: e.target.value })}
                  onBlur={() => touchField?.(`rules.${idx}.on_mismatch`)}
                  renderValue={(v) => v || (
                    <Typography component="span" sx={{ color: colors.neutral[600] }}>deny</Typography>
                  )}
                  sx={{ ...asecSelectSx, ...asecFormRowControlSx }}>
                  {ACTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
                {mismatchErr ? <FormHelperText>{mismatchErr}</FormHelperText> : null}
              </FormControl>
            </AsecField>
          </Grid>
        </Grid>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap
          sx={{ p: 1.5, borderRadius: "8px", border: `1px dashed ${colors.neutral[300]}`, fontSize: 12 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: colors.neutral[700] }}>if</Typography>
          <ExprTok kind="claim">{rule.claim || "claim"}</ExprTok>
          <ExprTok kind="op">{rule.op || "eq"}</ExprTok>
          <ExprTok kind="param">{`{${rule.param || "param"}}`}</ExprTok>
          <Typography sx={{ color: colors.neutral[600] }}>→</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: colors.neutral[700] }}>else</Typography>
          <ExprTok kind="act">{rule.on_mismatch || "deny"}</ExprTok>
        </Stack>
        <AsecField
          label="Exempt scopes"
          optHint="· comma separated"
          helper="Identities holding any of these scopes bypass this rule entirely."
        >
          <AsecTextField fullWidth
            placeholder={AUTHZ_PLACEHOLDERS.exempt_scopes}
            value={(rule.exempt_scopes || []).join(", ")}
            onChange={(e) => onChange({
              exempt_scopes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
            })}
          />
        </AsecField>
      </Stack>
    </Box>
  );
}

function PolicyDrawer({
  open, draft, setDraft, isNew, onClose, onSave, onDelete, saving, canDelete,
  showFieldErr, touchField, showRuleFieldErr,
}) {
  if (!draft) return null;
  const methodErr = showFieldErr?.("method");
  const pathErr = showFieldErr?.("path_template");
  const modeErr = showFieldErr?.("mode");
  const rulesErr = showFieldErr?.("rules");

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 520, maxWidth: "94vw", backgroundColor: "#fff" } }}>
      <Stack sx={{ height: "100%", backgroundColor: "#fff" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between"
          sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${colors.neutral[200]}`, gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "nowrap" }}>
              <LayerdIcon icon={ShieldPlus} size="sm" color="primary" />
              <Typography sx={{ fontSize: 16, fontWeight: 500, color: colors.neutral[1000] }}>
                {isNew ? "New authorization policy" : "Edit authorization policy"}
              </Typography>
              <FormHelpTooltip tooltip="Define a BOLA/IDOR ownership rule: match a route and compare a JWT claim to a URL path parameter. First deny short-circuits; enforce returns 403, log only records the mismatch." />
            </Stack>
            <Typography sx={{ fontSize: 13, color: colors.neutral[700], mt: 0.75, lineHeight: "19px" }}>
              Ownership rules are evaluated top-down. First match that denies short-circuits the request.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={asecIconButtonSx}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Stack spacing={3} sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, backgroundColor: "#fff" }}>
          <Box>
            <Grid container columnSpacing={2} rowSpacing={1}>
              <Grid item xs={4}>
                <Typography sx={{ ...asecFieldLabelSx, mb: 0 }}>Method</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography sx={{ ...asecFieldLabelSx, mb: 0 }}>Path template</Typography>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small" error={Boolean(methodErr)}>
                  <Select displayEmpty value={draft.method || ""} disabled={!isNew}
                    onChange={(e) => setDraft({ ...draft, method: e.target.value })}
                    onBlur={() => touchField?.("method")}
                    renderValue={(v) => v || (
                      <Typography component="span" sx={{ color: colors.neutral[600] }}>{AUTHZ_PLACEHOLDERS.method}</Typography>
                    )}
                    sx={{ ...asecSelectSx, ...asecFormRowControlSx }}>
                    {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </Select>
                  {methodErr ? <FormHelperText>{methodErr}</FormHelperText> : null}
                </FormControl>
              </Grid>
              <Grid item xs={8}>
                <AsecTextField
                  compact
                  fullWidth
                  disabled={!isNew}
                  placeholder={AUTHZ_PLACEHOLDERS.path_template}
                  value={draft.path_template || ""}
                  onChange={(e) => setDraft({ ...draft, path_template: e.target.value })}
                  onBlur={() => touchField?.("path_template")}
                  error={Boolean(pathErr)}
                  helperText={pathErr || ""}
                  sx={asecFormRowControlSx}
                />
              </Grid>
            </Grid>
            <Typography sx={{ fontSize: 12, color: colors.neutral[600], lineHeight: "25px", mt: 1 }}>
              OpenAPI-style. Parameters in <AsecMono>{"{curly braces}"}</AsecMono>.
            </Typography>
          </Box>

          <Box>
            <AsecField
              label="Enforcement mode"
              helper={(
                <>
                  In <strong>Log only</strong>, mismatches are recorded but requests still reach origin.
                  {" "}<strong>Enforce</strong> denies with 403 + RFC 7807.
                </>
              )}
            >
              <AsecModeSegmented
                value={draft.mode}
                onChange={(v) => {
                  touchField?.("mode");
                  setDraft({ ...draft, mode: v });
                }}
                options={AUTHZ_MODE_OPTIONS}
                sx={{
                  backgroundColor: "#fff",
                  ...(modeErr ? {
                    outline: `1px solid ${colors.red[200]}`,
                    outlineOffset: 2,
                  } : {}),
                }}
              />
            </AsecField>
            {modeErr ? (
              <FormHelperText error sx={{ mt: 0.75, mx: 0 }}>{modeErr}</FormHelperText>
            ) : null}
          </Box>

          <Box>
            {rulesErr ? (
              <FormHelperText error sx={{ mb: 1, mx: 0 }}>{rulesErr}</FormHelperText>
            ) : null}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[1000] }}>
                  Ownership rules
                </Typography>
                <AsecStatusPill>{(draft.rules || []).length}</AsecStatusPill>
              </Stack>
              <GhostButton size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setDraft({
                  ...draft,
                  rules: [...(draft.rules || []), { ...EMPTY_RULE }],
                })}>Add rule</GhostButton>
            </Stack>

            {(draft.rules || []).map((rule, idx) => (
              <RuleCard key={idx} rule={rule} idx={idx} totalRules={(draft.rules || []).length}
                showRuleFieldErr={showRuleFieldErr}
                touchField={touchField}
                onChange={(patch) => setDraft({
                  ...draft,
                  rules: draft.rules.map((r, i) => i === idx ? { ...r, ...patch } : r),
                })}
                onRemove={() => setDraft({
                  ...draft,
                  rules: draft.rules.filter((_, i) => i !== idx),
                })}
              />
            ))}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.neutral[200]}`, backgroundColor: "#fff", gap: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.875} sx={{ fontSize: 12, color: colors.neutral[700] }}>
            <LayerdIcon icon={GitBranch} size="xs" color="neutral" /> First-deny short-circuits
          </Stack>
          <Stack direction="row" spacing={1}>
            {!isNew && canDelete && (
              <GhostButton startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} onClick={onDelete}
                sx={{ color: "#A11228", borderColor: "#FBC9CF" }}>Delete</GhostButton>
            )}
            <GhostButton onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton startIcon={<CheckIcon sx={{ fontSize: 16 }} />} onClick={onSave} disabled={saving}
              sx={asecFormPrimaryButtonSx}>
              {saving ? "Saving…" : (isNew ? "Create policy" : "Save policy")}
            </PrimaryButton>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
}

function emptyPolicy() {
  return {
    method: "GET",
    path_template: "",
    mode: "enforce",
    rules: [{ ...EMPTY_RULE }],
  };
}

function Body({ siteId, onRegisterOpenNew }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure, canWrite, canDelete } = useAsecPermissions();
  const [tab, setTab] = React.useState(0);
  const [rows, setRows] = React.useState([]);
  const [denialsMap, setDenialsMap] = React.useState({});
  const [drawer, setDrawer] = React.useState(null);
  const [pendingDelete, setPendingDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [touched, setTouched] = React.useState({});
  const tabKeys = ["policies", "denials", "suggestions"];

  const resetValidation = React.useCallback(() => {
    setSubmitAttempted(false);
    setTouched({});
  }, []);

  const formValidation = React.useMemo(
    () => validateAuthzPolicyForm(drawer?.draft || {}, { isNew: drawer?.mode === "new" }),
    [drawer?.draft, drawer?.mode],
  );

  const touchField = React.useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = React.useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  const showRuleFieldErr = React.useCallback(
    (idx, field) => (submitAttempted || touched[`rules.${idx}.${field}`])
      && formValidation.ruleErrors[idx]?.[field],
    [submitAttempted, touched, formValidation.ruleErrors],
  );

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const list = await asec.authzList(siteId);
      setRows(list.data?.items || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
    try {
      const d = await asec.authzDenials(siteId, { hours: 24 });
      const dmap = {};
      for (const r of (d.data?.items || [])) {
        dmap[r.policy_id] = Number(r.denials_24h || 0);
      }
      setDenialsMap(dmap);
    } catch {
      setDenialsMap({});
    }
  }, [siteId, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  const openNew = React.useCallback((seed) => {
    if (!canWrite) return;
    resetValidation();
    setDrawer({ mode: "new", row: null, draft: seed || emptyPolicy() });
  }, [canWrite, resetValidation]);
  React.useEffect(() => {
    if (onRegisterOpenNew) onRegisterOpenNew(() => openNew());
  }, [onRegisterOpenNew, openNew]);
  const openEdit = (row) => {
    resetValidation();
    setDrawer({
      mode: "edit", row,
      draft: {
        method: row.method,
        path_template: row.path_template,
        mode: row.mode,
        rules: (row.rules && row.rules.length) ? row.rules : [{ ...EMPTY_RULE }],
      },
    });
  };
  const closeDrawer = () => {
    resetValidation();
    setDrawer(null);
  };
  const setDraft = (next) => setDrawer((d) => d ? { ...d, draft: next } : d);

  const save = async () => {
    if (!drawer || !canConfigure) return;
    setSubmitAttempted(true);
    if (!formValidation.valid) return;

    setSaving(true);
    try {
      if (drawer.mode === "new") {
        await asec.authzCreate(siteId, drawer.draft);
        showSuccess("Policy created.");
      } else {
        await asec.authzPatch(siteId, drawer.row.id, { mode: drawer.draft.mode, rules: drawer.draft.rules });
        showSuccess("Policy updated.");
      }
      closeDrawer();
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (row) => {
    if (!canDelete || !row) return;
    setPendingDelete(row);
  };

  const executeDelete = async () => {
    const row = pendingDelete;
    if (!row || !canDelete) return;
    setDeleting(true);
    try {
      await asec.authzDelete(siteId, row.id);
      showSuccess("Policy deleted.");
      setPendingDelete(null);
      if (drawer?.row?.id === row.id) closeDrawer();
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const removeFromDrawer = () => {
    if (!drawer || drawer.mode !== "edit" || !canDelete) return;
    requestDelete(drawer.row);
  };

  const counts = {
    policies: rows.length,
    denials: rows.reduce((a, r) => a + (Number(denialsMap[r.id]) || 0), 0) || SAMPLE_DENIALS.length,
    suggestions: SAMPLE_SUGGESTIONS.length,
  };

  return (
    <Stack spacing={2.5}>
      <AsecMetricStrip metrics={buildHeroMetrics(rows, denialsMap)} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={asecTabsSx}>
        <Tab label={`Policies (${counts.policies})`} icon={<LayerdIcon icon={ListChecks} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Denial log (${counts.denials})`} icon={<LayerdIcon icon={ShieldX} size="sm" color="inherit" />} iconPosition="start" />
        <Tab label={`Suggestions (${counts.suggestions})`} icon={<LayerdIcon icon={Sparkles} size="sm" color="inherit" />} iconPosition="start" />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: -1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[300] }} />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>Live · last sync 4s ago</Typography>
      </Stack>

      {tabKeys[tab] === "policies" && (
        <PoliciesTab siteId={siteId} rows={rows} denialsMap={denialsMap}
          onEdit={openEdit}
          onNew={(seed) => openNew(seed && {
            method: seed.method, path_template: seed.path_template,
            mode: seed.mode, rules: seed.rules,
          })}
          onDelete={requestDelete}
          canWrite={canWrite} canDelete={canDelete} />
      )}
      {tabKeys[tab] === "denials" && <DenialsTab siteId={siteId} rows={rows} denialsMap={denialsMap} />}
      {tabKeys[tab] === "suggestions" && <SuggestionsTab onCreate={openNew} />}

      <PolicyDrawer
        open={!!drawer}
        draft={drawer?.draft}
        setDraft={setDraft}
        isNew={drawer?.mode === "new"}
        canDelete={canDelete}
        onClose={closeDrawer}
        onSave={save}
        onDelete={removeFromDrawer}
        saving={saving}
        showFieldErr={showFieldErr}
        touchField={touchField}
        showRuleFieldErr={showRuleFieldErr}
      />

      <DeleteConfirmModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={executeDelete}
        title="Delete authorization policy"
        loading={deleting}
      >
        Are you sure you want to delete{" "}
        <strong>{pendingDelete?.method} {pendingDelete?.path_template}</strong>? This action cannot be undone.
      </DeleteConfirmModal>
    </Stack>
  );
}

export default function AsecAuthorization() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const { canWrite } = useAsecPermissions();
  const openNewRef = React.useRef(null);

  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="Per-route BOLA/IDOR ownership rules. JWT claim vs URL path-template parameter. First-Deny short-circuits. Mode enforce returns 403 + RFC 7807; mode log records the mismatch in layerd-v2-auth-events without blocking."
      title="Authorization"
      helmetTitle="API Security — Authorization"
      pageDescription={(
        <>
          Per-route ownership rules. Compares a JWT claim (e.g. <AsecMono>org_id</AsecMono>) against a URL path-template
          parameter (e.g. <AsecMono>{"{orgId}"}</AsecMono>). First-Deny short-circuits. <strong>Enforce</strong> returns{" "}
          <AsecMono>403 + RFC 7807</AsecMono>; <strong>Log</strong> records the mismatch as <AsecMono>ownership_mismatch</AsecMono>{" "}
          but lets the request through.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
      headerActions={(
        <PrimaryButton startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => openNewRef.current?.()}
          disabled={!canWrite}
        >
          New policy
        </PrimaryButton>
      )}
    >
      {(siteId) => (
        <Body
          key={refreshKey}
          siteId={siteId}
          onRegisterOpenNew={(fn) => { openNewRef.current = fn; }}
        />
      )}
    </AsecPage>
  );
}
